import { useEffect, useMemo, useState } from 'react';
import { PostComposer } from '@/components/feed/PostComposer';
import { PostCard } from '@/components/feed/PostCard';
import { PostWithProfile, usePostsPaged } from '@/hooks/usePosts';
import { useBlocks } from '@/hooks/useBlocks';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { MemberAvatarBar } from '@/components/feed/MemberAvatarBar';
import { FeedSidebar } from '@/components/feed/FeedSidebar';

export default function FeedPage() {
  const [page, setPage] = useState(0);
  const pageSize = 20;
  const { data: pageData, isLoading, isError, error } = usePostsPaged({
    cohortTag: 'all',
    page,
    pageSize,
  });
  const { data: blocks } = useBlocks();

  const [aggregatedPosts, setAggregatedPosts] = useState<PostWithProfile[]>([]);

  useEffect(() => {
    if (!pageData?.data) return;
    if (page === 0) {
      setAggregatedPosts(pageData.data);
      return;
    }
    setAggregatedPosts((current) => {
      const existingIds = new Set(current?.map((p) => p.id) || []);
      const next = pageData.data.filter((p) => !existingIds.has(p.id));
      return [...(current || []), ...next];
    });
  }, [pageData, page]);

  // Memoize blocked IDs set and filtered posts to prevent unnecessary re-renders
  const posts = useMemo(() => {
    if (!aggregatedPosts) return [];
    const blockedIds = new Set(blocks?.map((b) => b.blocked_id) || []);
    return aggregatedPosts.filter((p) => !blockedIds.has(p.user_id));
  }, [aggregatedPosts, blocks]);

  const totalCount = pageData?.count ?? 0;
  const canLoadMore = posts.length < totalCount;

  return (
    <div className="xl:grid xl:grid-cols-[minmax(0,1fr)_280px] xl:gap-6">
      <div className="space-y-6 min-w-0">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-display font-bold text-foreground">Home</h1>
          <p className="text-muted-foreground text-sm">
            Real connections. Real outcomes.
          </p>
        </div>

        <MemberAvatarBar />

        {isError && (
          <div className="rounded-lg border border-destructive/20 bg-destructive/5 p-3 text-sm text-destructive">
            {error instanceof Error ? error.message : 'Unable to load posts right now.'}
          </div>
        )}

        {/* Post Composer */}
        <PostComposer cohortTag="global" />

        {/* Posts */}
        {isLoading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : posts && posts.length > 0 ? (
          <div className="space-y-4">
            {posts.map((post) => (
              <PostCard key={post.id} post={post} />
            ))}
            {canLoadMore && (
              <div className="flex justify-center pt-2">
                <Button
                  variant="outline"
                  onClick={() => setPage((p) => p + 1)}
                >
                  Load more
                </Button>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-12">
            <p className="text-muted-foreground">
              No posts yet. Be the first to share something!
            </p>
          </div>
        )}
      </div>
      <FeedSidebar />
    </div>
  );
}
